import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { providersAPI, jobsAPI } from '../api';

const STATUS_COLORS = { escrow:'#6DBE45',active:'#00E676',complete:'#1E7FD4',cancelled:'#FF4757' };

export default function ProviderDashboard() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);
  const [hours, setHours] = useState('');
  const nav = useNavigate();

  const load = () => {
    providersAPI.dashboard().then(r=>setData(r.data)).catch(()=>{}).finally(()=>setLoading(false));
  };
  useEffect(load, []);

  const handleComplete = async (jobId) => {
    setCompleting(jobId);
    try { await jobsAPI.complete(jobId, { hours_actual: hours ? parseFloat(hours) : undefined }); load(); }
    catch(e) { alert(e.response?.data?.error||'Failed'); }
    setCompleting(null); setHours('');
  };

  const handleStripeOnboard = async () => {
    try { const r = await providersAPI.stripeOnboard(); window.location.href = r.data.url; }
    catch(e) { alert('Stripe onboarding failed: '+e.message); }
  };

  if (loading) return <div style={{textAlign:'center',padding:80}}><span className="spinner"/></div>;
  const { provider: p, jobs, mtd_earnings, avg_rating, review_count, market_rate } = data || {};

  return (
    <div className="page-container">
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:28,flexWrap:'wrap',gap:12}}>
        <div>
          <div className="section-title">My Dashboard</div>
          <div className="section-sub">{p?.code_id} · {p?.market_name}</div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          <span style={{padding:'5px 10px',borderRadius:4,fontSize:11,fontWeight:700,
            background:p?.active?'rgba(0,230,118,0.12)':'rgba(245,166,35,0.12)',
            color:p?.active?'#00E676':'#6DBE45'}}>
            {p?.active?'● Active':'⏳ Pending Approval'}
          </span>
          {!p?.stripe_onboarded && (
            <button className="btn btn-gold btn-sm" onClick={handleStripeOnboard}>Set Up Payments →</button>
          )}
        </div>
      </div>

      {!p?.stripe_onboarded && (
        <div className="alert alert-info" style={{marginBottom:24}}>
          ⚠ Complete your Stripe onboarding to receive job payments.
          <button className="btn btn-sm btn-gold" style={{marginLeft:12}} onClick={handleStripeOnboard}>Set Up Now →</button>
        </div>
      )}

      {/* Stats */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:12,marginBottom:28}}>
        {[
          { label:'MTD Earnings', val:`$${parseFloat(mtd_earnings||0).toFixed(2)}`, color:'#00E676', sub:'After 10% fee' },
          { label:'Active Jobs',  val:(jobs||[]).filter(j=>j.status==='active').length, color:'#1E7FD4', sub:'Currently running' },
          { label:'Rating',       val:avg_rating>0?`${avg_rating} ★`:'New', color:'#6DBE45', sub:`${review_count||0} reviews` },
          { label:'Your Rate',    val:`$${p?.rate_hr}/hr`, color:'#F0F6FF', sub:market_rate?`Market avg: $${parseFloat(market_rate.rate_avg).toFixed(0)}`:'—' },
          { label:'Total Jobs',   val:(jobs||[]).filter(j=>j.status==='complete').length, color:'#F0F6FF', sub:'Completed' },
          { label:'Tier',         val:(p?.tier||'—').toUpperCase(), color:p?.tier==='elite'?'#6DBE45':p?.tier==='certified'?'#1E7FD4':'#8899AA', sub:'Listing priority' },
        ].map(s=>(
          <div key={s.label} className="card" style={{padding:18}}>
            <div style={{fontSize:11,color:'#556677',textTransform:'uppercase',letterSpacing:1,marginBottom:6}}>{s.label}</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:28,fontWeight:800,color:s.color}}>{s.val}</div>
            <div style={{fontSize:11,color:'#445566',marginTop:3}}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Market rate context */}
      {market_rate && (
        <div className="card" style={{marginBottom:24,padding:18}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:18,fontWeight:700,marginBottom:14}}>
            Market Rates — {p?.market_name}
          </div>
          <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
            {[
              {l:'Market Low',  v:`$${parseFloat(market_rate.rate_low).toFixed(0)}`},
              {l:'Market Avg',  v:`$${parseFloat(market_rate.rate_avg).toFixed(0)}`},
              {l:'Your Rate',   v:`$${p?.rate_hr}`, hi:true},
              {l:'Market High', v:`$${parseFloat(market_rate.rate_high).toFixed(0)}`},
            ].map(r=>(
              <div key={r.l} style={{flex:1,minWidth:80,textAlign:'center',padding:'12px 8px',
                background:r.hi?'rgba(0,194,255,0.08)':'rgba(255,255,255,0.02)',
                border:`1px solid ${r.hi?'rgba(0,194,255,0.3)':'rgba(255,255,255,0.05)'}`,borderRadius:8}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:800,color:r.hi?'#1E7FD4':'#F0F6FF'}}>{r.v}</div>
                <div style={{fontSize:10,color:'#445566',marginTop:2}}>{r.l}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Jobs */}
      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20,fontWeight:700,marginBottom:14}}>Recent Jobs</div>
      {(jobs||[]).length===0 ? (
        <div className="card" style={{textAlign:'center',padding:'40px 20px',color:'#445566'}}>
          No jobs yet. Complete your profile and payment setup to start receiving bookings.
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {(jobs||[]).map(j=>(
            <div key={j.id} className="card" style={{padding:18}}>
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexWrap:'wrap',gap:10}}>
                <div style={{flex:1}}>
                  <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:6,flexWrap:'wrap'}}>
                    <span style={{fontWeight:600,fontSize:14,color:'#F0F6FF'}}>{j.service_name}</span>
                    <span style={{fontSize:11,fontWeight:700,padding:'3px 8px',borderRadius:4,textTransform:'uppercase',
                      background:`${STATUS_COLORS[j.status]||'#8899AA'}18`,color:STATUS_COLORS[j.status]||'#8899AA',
                      border:`1px solid ${STATUS_COLORS[j.status]||'#8899AA'}44`}}>{j.status}</span>
                  </div>
                  <div style={{fontSize:13,color:'#8899AA'}}>
                    {j.market_name} · {j.start_date?new Date(j.start_date).toLocaleDateString():'TBD'} · {j.hours_estimated}h
                  </div>
                  {j.customer_email && (
                    <div style={{fontSize:13,color:'#AAB8C8',marginTop:4}}>
                      Customer: {j.first_name} {j.last_name} {j.company&&`· ${j.company}`}
                      <br/><span style={{color:'#556677'}}>{j.customer_email}{j.customer_phone&&` · ${j.customer_phone}`}</span>
                    </div>
                  )}
                </div>
                <div style={{textAlign:'right',flexShrink:0}}>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:22,fontWeight:800,color:'#00E676'}}>
                    ${parseFloat(j.provider_payout||0).toFixed(2)}
                  </div>
                  <div style={{fontSize:11,color:'#445566'}}>your payout</div>
                  {j.status==='active' && (
                    <div style={{marginTop:8,display:'flex',gap:6,alignItems:'center',flexWrap:'wrap',justifyContent:'flex-end'}}>
                      <input type="number" placeholder="Actual hrs" style={{width:100,fontSize:12,padding:'5px 8px'}}
                        value={completing===j.id?hours:''} onChange={e=>setHours(e.target.value)}
                        onFocus={()=>setCompleting(j.id)}/>
                      <button className="btn btn-sm btn-primary"
                        onClick={()=>handleComplete(j.id)}>Mark Complete</button>
                    </div>
                  )}
                </div>
              </div>
              <div style={{fontSize:11,color:'#334455',marginTop:8}}>Job #{j.id?.slice(0,8).toUpperCase()}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
